import { useMemo, useState } from 'react';

import { useMe } from '../hooks/useAuth.js';
import { useRecentWorkouts } from '../hooks/useStats.js';

export interface ReceiptsWorkoutBlockProps {
  weekStart: string;
  weekEnd: string;
}

/** Tracker-first workout receipt: completed sessions versus the user's weekly goal. */
export function ReceiptsWorkoutBlock({ weekStart, weekEnd }: ReceiptsWorkoutBlockProps) {
  const [open, setOpen] = useState(false);
  const me = useMe();
  const workouts = useRecentWorkouts(50);
  const rows = useMemo(() => {
    const start = new Date(weekStart).getTime();
    const end = new Date(weekEnd).getTime();
    return (workouts.data?.items ?? [])
      .filter((workout) => {
        const at = new Date(workout.date).getTime();
        return at >= start && at <= end;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [weekEnd, weekStart, workouts.data?.items]);

  const goal = me.data?.profile.weeklyGoal ?? 0;
  const summary = goal > 0 ? `${rows.length} of ${goal} sessions` : `${rows.length} completed`;

  return (
    <article className="review-receipt">
      <button
        type="button"
        className="review-receipt__toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="review-receipt__title">Workouts</span>
        <span className="review-receipt__summary">{summary}</span>
        <span className="review-receipt__chevron" aria-hidden>
          ▾
        </span>
      </button>

      {open ? (
        <div className="review-receipt__body">
          {rows.length === 0 ? (
            <p className="review-receipt__empty">No completed workouts this week.</p>
          ) : (
            <ul className="review-workout">
              {rows.map((workout) => (
                <li key={workout.id} className="review-workout__row review-workout__row--completed">
                  <span className="review-workout__date">{formatDate(workout.date)}</span>
                  <span className="review-workout__session">{workout.title}</span>
                  <span className="review-workout__status" aria-label="completed">
                    ✓
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </article>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}
